import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import AuthRoutes from "./routes/AuthRoutes";
import Dashboard from "./pages/dashboard/Dashboard";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import { Login } from "./pages";
import dashboardRoutes, { DashbordRoute } from "./routes/adminRoutes";
import userRoutes from "./routes/userRoutes";
import atcRoutes from "./routes/atcRoutes";
import AuthenticateRoute from "./routes/AuthenticationRoute";
import { ConfigProvider } from "antd";
import axiosClient, { isStaleAuthFailure } from "./api/axiosClient";
import {
  clearPendingRedirect,
  readPendingRedirect,
} from "./utils/pendingRedirect";
import { getAuthState } from "./redux/store/getState";
import CallBackLogin from "./pages/auth/CallBackLogin";
import { useEffect } from "react";
import { getMe } from "./redux/apiActions/authActions";
import theme from "./utils/theme";
import "./App.css";
import ErrorBoundary from "./components/ErrorBoundary";
import { store } from "./redux/store";
import { logout } from "./redux/apiSlice/authSlice";
import Maintenence from "./pages/maintenance/Maintenence";

export default function App() {
  const { isAuthenticated, homeRoute, userType, maintenance } =
    useAppSelector(getAuthState);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    // While the SSO callback is exchanging its single-use token it owns the
    // session — CallBackLogin loads it itself. Firing getMe() here would race it
    // with the *previous* token, and that call's 403 used to tear down the freshly
    // minted session mid-handoff (dropping the student on the home page).
    if (pathname.startsWith("/auth")) return;

    if (isAuthenticated) {
      dispatch(getMe());
    } else if (!pathname.includes("/maintenance")) {
      navigate("/login");
    }
    // eslint-disable-next-line
  }, [isAuthenticated, dispatch]);

  // Safety net for the Xperience "Start Test" handoff: if it got knocked off course
  // (session re-auth, a reload, a trip through /login) the student would otherwise
  // land on their home route with no idea why. Once a session is live, resume the
  // parked target instead of stranding them.
  useEffect(() => {
    if (!userType) return;
    const target = readPendingRedirect();
    if (!target || target === pathname) return;
    // Only rescue from a landing page — never hijack real navigation.
    const stranded =
      pathname === "/" || pathname === homeRoute || pathname.startsWith("/login");
    if (!stranded) return;
    clearPendingRedirect();
    navigate(target, { replace: true });
    // eslint-disable-next-line
  }, [userType, homeRoute, pathname]);

  useEffect(() => {
    if (maintenance) {
      navigate("maintenance");
    }
    // eslint-disable-next-line
  }, [maintenance]);

  return (
    <ErrorBoundary>
      <ConfigProvider theme={theme}>
        <Routes>
          <Route path="/auth/*" element={<AuthRoutes />}>
            <Route path=":token" element={<CallBackLogin />} />
          </Route>
          <Route path="/maintenance" element={<Maintenence />} />
          <Route
            path="/login/*"
            element={
              <AuthenticateRoute
                isAuthenticated={isAuthenticated}
                redirectTo={homeRoute}
              >
                <AuthRoutes />
              </AuthenticateRoute>
            }
          >
            <Route path="" element={<Login />} />
          </Route>
          {userType === "admin" && (
            <Route
              path="/*"
              element={
                <AuthenticateRoute
                  isAuthenticated={!isAuthenticated}
                  redirectTo="/login"
                >
                  <Dashboard routes={dashboardRoutes} />
                </AuthenticateRoute>
              }
            >
              {dashboardRoutes.map(
                ({ Component, path, key }: DashbordRoute) => (
                  <Route key={key} path={path} element={<Component />} />
                )
              )}
            </Route>
          )}
          {userType === "atc" && (
            <Route
              path="/*"
              element={
                <AuthenticateRoute
                  isAuthenticated={!isAuthenticated}
                  redirectTo="/login"
                >
                  <Dashboard routes={atcRoutes} />
                </AuthenticateRoute>
              }
            >
              {atcRoutes.map(({ Component, path, key }: DashbordRoute) => (
                <Route key={key} path={path} element={<Component />} />
              ))}
            </Route>
          )}

          {userType === "student" && (
            <Route
              path="/*"
              element={
                <AuthenticateRoute
                  isAuthenticated={!isAuthenticated}
                  redirectTo="/login"
                >
                  <Dashboard routes={userRoutes} />
                </AuthenticateRoute>
              }
            >
              {userRoutes.map(({ Component, path, key }: DashbordRoute) => (
                <Route key={key} path={path} element={<Component />} />
              ))}
            </Route>
          )}
        </Routes>
      </ConfigProvider>
    </ErrorBoundary>
  );
}

// Logout when the API reports an invalid/expired session. The backend returns
// this in a couple of shapes ({status: "invalid auth"} or
// {status: "Invalid session Please login again"}), and if we don't catch every
// one the app is left "authenticated" while every request 403s -> blank page.
axiosClient.interceptors.response.use(
  (response) => {
    return response; // Return response if no error
  },
  (error) => {
    const status = error.response?.status;
    const apiStatus = String(error.response?.data?.status ?? "").toLowerCase();
    const isSessionError =
      (status === 401 || status === 403) &&
      (apiStatus === "invalid auth" ||
        apiStatus.includes("login again") ||
        apiStatus.includes("invalid session"));

    // Ignore failures signed with a token we have already replaced: during the SSO
    // handoff an old request can land after the new session is installed, and
    // logging out on it would kill the session the student is about to exam with.
    if (isSessionError && !isStaleAuthFailure(error.config)) {
      store.dispatch(logout());
    }
    return Promise.reject(error); // Return error to be handled by catch block
  }
);
